/**
 * AccessGate — themed authentication overlay (Sign In + Sign Up tabs).
 *
 * UX/UI ported from the EE Data Assistant (EE-DATA-ASSISTANT-V4). Adds a
 * multi-tenant "Workspace" (org slug) field on top so the same form
 * doubles as the entry point for any tenant subdomain / sub-path. The
 * theme is fully driven by ThemeContext + CSS variables, so the gate
 * automatically picks up Silver/Gold/Dark palettes without inline
 * overrides.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Button, Typography, Alert, Tabs, Modal, message, Divider } from 'antd';
import {
  MailOutlined,
  LockOutlined,
  UserOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  WindowsFilled,
  BankOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  registerUser,
  getValidationPolicy,
  resendVerification,
  getEntraSsoConfig,
  startEntraLogin,
} from '../services/authService';
import { getTenantSlugFromUrl } from '../utils/tenantPath';
import { isSingleTenant, getDeploymentConfig } from '../services/deploymentConfigService';
import './AccessGate.css';

const EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED';

const { Title, Text, Paragraph } = Typography;

// ── pure helpers (workspace resolution, form validation, error display) ──

// Pulls the `organization_id` claim out of a JWT access token — never
// throws, returns '' on anything malformed. Kept separate from the
// localStorage lookup below so neither has to nest inside the other.
// Base64 encodes in 4-character blocks; padding fills the last block.
const BASE64_BLOCK_LEN = 4;

export function extractOrgIdFromJwt(token) {
  if (!token) {return '';}
  const parts = token.split('.');
  if (parts.length < 2) {return '';}
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '==='.slice((b64.length + BASE64_BLOCK_LEN - 1) % BASE64_BLOCK_LEN);
    const claims = JSON.parse(atob(padded));
    return (claims && typeof claims.organization_id === 'string') ? claims.organization_id : '';
  } catch {
    return '';
  }
}

// Resolution order for the cached workspace slug when there's no URL slug:
// a still-present token's JWT claim (covers "session expired but cached
// token survives"), else the last successful login's cached slug.
export function resolveCachedWorkspaceSlug() {
  if (typeof window === 'undefined') {return '';}
  try {
    const orgId = extractOrgIdFromJwt(localStorage.getItem('authToken'));
    if (orgId) {return orgId;}
  } catch { /* fall through */ }
  try { return localStorage.getItem('ee_assistant_last_workspace') || ''; } catch { return ''; }
}

// In single-tenant deployments the workspace is fixed — backend pins every
// request to DEFAULT_TENANT_ID regardless of what the client sends.
export function computeSingleTenantSlug(singleTenant) {
  return singleTenant ? (getDeploymentConfig().default_tenant_id || '') : '';
}

export function computeInitialWorkspace(singleTenant, singleTenantSlug, urlSlug, cachedSlug) {
  return singleTenant ? singleTenantSlug : (urlSlug || cachedSlug || '');
}

// SSO callback errors are surfaced by AuthContext as `SSO_ERROR:<code>`.
export function extractSsoErrorCode(authError) {
  return (typeof authError === 'string' && authError.startsWith('SSO_ERROR:'))
    ? authError.slice('SSO_ERROR:'.length)
    : null;
}

const SSO_ERROR_MESSAGES = {
  pending_approval: 'Your account is awaiting administrator approval.',
  account_rejected: 'Your access request was declined. Please contact your administrator.',
  account_disabled: 'Your account is disabled. Please contact your administrator.',
  domain_not_allowed: 'Your email domain is not allowed to sign in via Microsoft SSO.',
  no_email_in_token: 'Microsoft did not return an email address for your account.',
};

export function resolveSsoErrorMessage(ssoErrorCode) {
  if (!ssoErrorCode) {return null;}
  return SSO_ERROR_MESSAGES[ssoErrorCode] || `Microsoft sign-in failed (${ssoErrorCode}).`;
}

export function isPlainAuthError(isEmailNotVerified, isApprovalState, isSsoError) {
  return !isEmailNotVerified && !isApprovalState && !isSsoError;
}

// Which single message (if any) to show above the Sign In form — local
// validation first, then an SSO failure, then a generic auth error (unless
// it's already covered by a more specific banner below).
export function computeDisplayError({ localError, authError, isEmailNotVerified, isApprovalState, isSsoError, ssoErrorMessage }) {
  if (localError) {return localError;}
  const ssoMessage = isSsoError ? ssoErrorMessage : '';
  if (ssoMessage) {return ssoMessage;}
  const isGenericAuthError = authError && isPlainAuthError(isEmailNotVerified, isApprovalState, isSsoError);
  return isGenericAuthError ? authError : '';
}

// Sign-in form validation — pure, over already-entered field values.
export function computeEffectiveWorkspace(singleTenantNow, singleTenantSlugNow, workspace) {
  return singleTenantNow ? singleTenantSlugNow : workspace.trim();
}

export function validateSignInInput({ singleTenantNow, effectiveWorkspace, username, password }) {
  if (!singleTenantNow && !effectiveWorkspace) {return 'Workspace is required.';}
  if (!username.trim()) {return 'Username is required.';}
  if (!password.trim()) {return 'Password is required.';}
  return null;
}

// Whether a failed sign-in should open the "resend verification email" modal.
export function shouldPromptEmailVerification(loginResult) {
  if (loginResult.success) {return false;}
  const err = loginResult.error || '';
  return err === EMAIL_NOT_VERIFIED || err.includes(EMAIL_NOT_VERIFIED);
}

// Sign-up form validation, split from the password-policy checks below so
// neither grows past a handful of branches.
export function validateSignUpRequiredFields({ regEmail, regUsername, regPassword, regConfirm }) {
  if (!regEmail.trim() || !regUsername.trim() || !regPassword || !regConfirm) {
    return 'All fields are required.';
  }
  if (regPassword !== regConfirm) {
    return 'Passwords do not match.';
  }
  return null;
}

export function validatePasswordPolicy(password, policy) {
  if (!policy) {return null;}
  if (password.length < (policy.password_min_length || 8)) {
    return `Password must be at least ${policy.password_min_length} characters.`;
  }
  const rules = [
    [policy.password_require_uppercase, /[A-Z]/, 'Password must contain an uppercase letter.'],
    [policy.password_require_lowercase, /[a-z]/, 'Password must contain a lowercase letter.'],
    [policy.password_require_digit, /\d/, 'Password must contain a digit.'],
    [policy.password_require_special_char, /[!@#$%^&*(),.?":{}|<>]/, 'Password must contain a special character.'],
  ];
  for (const [required, pattern, msg] of rules) {
    if (required && !pattern.test(password)) {return msg;}
  }
  return null;
}

// The gate's accent colours, from the theme when there is one.
function gateColors(themeConfig) {
  return {
    iconColor: themeConfig?.primary || '#c9a961',
    labelColor: themeConfig?.textPrimary || '#1f2937',
    hintColor: themeConfig?.textSecondary || '#6b7280',
  };
}

// "Sign in with Microsoft SSO", above the password form on both tabs.
function SsoBlock({ workspace }) {
  return (
    <div style={{ marginBottom: 16 }} data-testid="entra-sso-block">
      <Button
        block
        size="large"
        icon={<WindowsFilled style={{ color: '#0078D4' }} />}
        onClick={() => {
          // Preserve the tenant prefix in the SSO round-trip so the user
          // lands back on /<slug>/... after Microsoft auth.
          const slug = (workspace || '').trim().toLowerCase();
          const fallback = slug ? `/${slug}/` : (window.location.pathname || '/');
          startEntraLogin(fallback);
        }}
        data-testid="entra-sso-button"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontWeight: 600 }}
      >
        Sign in with Microsoft SSO
      </Button>
      <Divider plain style={{ margin: '12px 0', fontSize: 12, color: '#888' }}>or</Divider>
    </div>
  );
}

// What the last sign-in attempt said: an error, an unverified email, a pending
// or declined access request.
function SignInAlerts({ displayError, onCloseError, isEmailNotVerified, onResend, isPendingApproval, isRejected }) {
  return (
    <>
      {displayError && (
        <Alert type="error" showIcon title={displayError}
          style={{ marginBottom: 16, borderRadius: 8 }} closable onClose={onCloseError} />
      )}
      {isEmailNotVerified && (
        <Alert
          type="warning"
          showIcon
          title="Email Not Verified"
          description={
            <span>
              Your account email has not been verified. Please check your inbox (including spam folder).{' '}
              <a onClick={onResend}
                style={{ cursor: 'pointer', fontWeight: 600 }}>
                Resend verification email
              </a>
            </span>
          }
          style={{ marginBottom: 16, borderRadius: 8 }}
          closable
          onClose={() => {}}
        />
      )}
      {isPendingApproval && (
        <Alert
          type="success"
          showIcon
          title="Awaiting Administrator Approval"
          description="Your access request has been sent to the administrators. You will receive an email once it is approved."
          style={{ marginBottom: 16, borderRadius: 8, backgroundColor: '#f6ffed', borderColor: '#b7eb8f', color: '#135200' }}
        />
      )}
      {isRejected && (
        <Alert
          type="error"
          showIcon
          title="Access Request Declined"
          description="Your access request was not approved. Please contact your administrator if you believe this was a mistake."
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}
    </>
  );
}

function SignInForm({
  singleTenant, workspaceLocked, workspace, setWorkspace, setLocalError, handleSignIn, isLoading,
  workspaceRef, usernameRef, username, setUsername, password, setPassword, labelColor, hintColor, iconColor,
}) {
  return (
    <Form layout="vertical" onFinish={handleSignIn} requiredMark={false} data-testid="login-form">
      {/* Workspace selector — only rendered in SaaS mode. In
          single-tenant the workspace is fixed and the backend
          pins all requests to DEFAULT_TENANT_ID regardless of
          any value the client sends. */}
      {!singleTenant && (
        <Form.Item
          label={<span className="access-gate-label" style={{ color: labelColor }}>Workspace</span>}
          extra={
            <span className="access-gate-extra" style={{ color: hintColor }}>
              {workspaceLocked
                ? <>From URL — sign in to <strong>/{workspace}/</strong></>
                : 'Your organization slug'}
            </span>
          }
        >
          <Input
            ref={workspaceRef}
            prefix={<BankOutlined style={{ color: iconColor }} />}
            value={workspace}
            onChange={e => { setWorkspace(e.target.value); setLocalError(''); }}
            onPressEnter={handleSignIn}
            placeholder="your-org"
            size="large"
            disabled={isLoading || workspaceLocked}
            className="access-gate-input"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            data-testid="workspace-input"
          />
        </Form.Item>
      )}
      <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Email or Username</span>}>
        <Input ref={usernameRef} prefix={<UserOutlined style={{ color: iconColor }} />}
          value={username} onChange={e => { setUsername(e.target.value); setLocalError(''); }}
          onPressEnter={handleSignIn} placeholder="Enter your email or username" size="large"
          disabled={isLoading} className="access-gate-input" allowClear
          autoComplete="username"
          data-testid="login-username-input" />
      </Form.Item>
      <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Password</span>}>
        <Input.Password prefix={<LockOutlined style={{ color: iconColor }} />}
          value={password} onChange={e => { setPassword(e.target.value); setLocalError(''); }}
          onPressEnter={handleSignIn} placeholder="Enter your password" size="large"
          disabled={isLoading} className="access-gate-input"
          autoComplete="current-password"
          iconRender={v => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
          data-testid="login-password-input" />
      </Form.Item>
      <Button type="primary" htmlType="submit" size="large" block
        disabled={(!singleTenant && !workspace.trim()) || !username.trim() || !password.trim()} loading={isLoading}
        className="access-gate-btn" data-testid="login-submit-button">
        {isLoading ? 'Signing in...' : 'Sign In'}
      </Button>
    </Form>
  );
}

// The password rules the sign-up form will check.
function PasswordPolicyHint({ policy, hintColor }) {
  if (!policy) {return null;}
  return (
    <div style={{ fontSize: 12, color: hintColor, marginBottom: 12 }}>
      Min {policy.password_min_length} chars
      {policy.password_require_uppercase && ', uppercase'}
      {policy.password_require_lowercase && ', lowercase'}
      {policy.password_require_digit && ', digit'}
      {policy.password_require_special_char && ', special char'}
    </div>
  );
}

function SignUpForm({
  regError, onCloseError, regSuccess, handleSignUp, regEmail, setRegEmail, regUsername, setRegUsername,
  regFullName, setRegFullName, regPassword, setRegPassword, regConfirm, setRegConfirm, policy, regLoading,
  labelColor, hintColor, iconColor,
}) {
  return (
    <>
      {regError && <Alert type="error" showIcon title={regError} style={{ marginBottom: 12, borderRadius: 8 }} closable onClose={onCloseError} />}
      {regSuccess && <Alert type="success" showIcon title={regSuccess} style={{ marginBottom: 12, borderRadius: 8, backgroundColor: '#f6ffed', borderColor: '#b7eb8f', color: '#135200' }} />}
      <Form layout="horizontal" onFinish={handleSignUp} requiredMark={false}
        labelCol={{ span: 9 }} wrapperCol={{ span: 15 }} labelAlign="left"
        data-testid="register-form">
        <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Email</span>}>
          <Input prefix={<MailOutlined style={{ color: iconColor }} />}
            value={regEmail} onChange={e => setRegEmail(e.target.value)}
            placeholder="you@company.com" size="large" className="access-gate-input"
            autoComplete="email"
            data-testid="register-email-input" />
        </Form.Item>
        <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Username</span>}>
          <Input prefix={<UserOutlined style={{ color: iconColor }} />}
            value={regUsername} onChange={e => setRegUsername(e.target.value)}
            placeholder="Choose a username" size="large" className="access-gate-input"
            autoComplete="username"
            data-testid="register-username-input" />
        </Form.Item>
        <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Full Name</span>}>
          <Input prefix={<UserOutlined style={{ color: iconColor }} />}
            value={regFullName} onChange={e => setRegFullName(e.target.value)}
            placeholder="Your full name (optional)" size="large" className="access-gate-input"
            autoComplete="name" />
        </Form.Item>
        <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Password</span>}>
          <Input.Password prefix={<LockOutlined style={{ color: iconColor }} />}
            value={regPassword} onChange={e => setRegPassword(e.target.value)}
            placeholder="Create a password" size="large" className="access-gate-input"
            autoComplete="new-password"
            iconRender={v => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
            data-testid="register-password-input" />
        </Form.Item>
        <Form.Item label={<span className="access-gate-label" style={{ color: labelColor }}>Confirm Password</span>}>
          <Input.Password prefix={<LockOutlined style={{ color: iconColor }} />}
            value={regConfirm} onChange={e => setRegConfirm(e.target.value)}
            placeholder="Confirm your password" size="large" className="access-gate-input"
            autoComplete="new-password"
            iconRender={v => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
            data-testid="register-confirm-password-input" />
        </Form.Item>
        <PasswordPolicyHint policy={policy} hintColor={hintColor} />
        <Button type="primary" htmlType="submit" size="large" block
          loading={regLoading} className="access-gate-btn" data-testid="register-submit-button">
          {regLoading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </Form>
    </>
  );
}

function AccessGate() {
  const { login, authError, isLoading } = useAuth();
  const { themeConfig } = useTheme() || {};
  const [activeTab, setActiveTab] = useState('signin');

  // Workspace (tenant slug) — drives both per-tenant routing AND which
  // org the token is scoped to on the backend.
  //
  // Resolution order:
  //   1. URL slug (when the user landed on /<slug>/...) — locked.
  //   2. JWT claim from a still-present token in localStorage. Covers
  //      the "session expired but cached token survives" case so the
  //      user doesn't have to retype their org.
  //   3. Cached login slug from a previous successful sign-in.
  //   4. Empty.
  const urlSlug = getTenantSlugFromUrl();
  const cachedSlug = resolveCachedWorkspaceSlug();
  // In single-tenant deployments the workspace is fixed and invisible to
  // the user — backend pins every request to DEFAULT_TENANT_ID regardless.
  const _singleTenant = isSingleTenant();
  const _singleTenantSlug = computeSingleTenantSlug(_singleTenant);
  const [workspace, setWorkspace] = useState(
    computeInitialWorkspace(_singleTenant, _singleTenantSlug, urlSlug, cachedSlug)
  );
  const workspaceLocked = Boolean(urlSlug) || _singleTenant;

  // Sign In state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const usernameRef = useRef(null);
  const workspaceRef = useRef(null);

  // Sign Up state
  const [regEmail, setRegEmail] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regFullName, setRegFullName] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');
  const [policy, setPolicy] = useState(null);

  // Verification resend
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [verifyUsername, setVerifyUsername] = useState('');

  // Microsoft Entra SSO availability — controlled by the backend
  // (/deployment/config -> entra.enabled). When false the button is hidden so password
  // login is the only option.
  const [ssoEnabled, setSsoEnabled] = useState(false);

  useEffect(() => {
    // Focus the first empty field — workspace if blank, else username.
    if (!workspace && workspaceRef.current) {
      workspaceRef.current.focus();
    } else if (usernameRef.current) {
      usernameRef.current.focus();
    } else {
      // Neither ref is mounted yet — nothing to focus.
    }
    getValidationPolicy().then(p => p && setPolicy(p)).catch(() => {});
    getEntraSsoConfig().then(cfg => setSsoEnabled(Boolean(cfg && cfg.enabled))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface SSO callback errors (set by AuthContext after the redirect).
  const ssoErrorMatch = extractSsoErrorCode(authError);
  const ssoErrorMessage = resolveSsoErrorMessage(ssoErrorMatch);

  // ── Sign In ───────────────────────────────────────────────
  const handleSignIn = async () => {
    if (isLoading) {return;}
    // In single-tenant mode the workspace selector is hidden and the slug
    // is sourced from /deployment/config. The fetch may have completed AFTER
    // initial render, so re-read it here (fresh) instead of trusting the
    // useState seed.
    const singleTenantNow = isSingleTenant();
    const singleTenantSlugNow = computeSingleTenantSlug(singleTenantNow).toLowerCase();
    const effectiveWorkspace = computeEffectiveWorkspace(singleTenantNow, singleTenantSlugNow, workspace);

    const validationError = validateSignInInput({ singleTenantNow, effectiveWorkspace, username, password });
    if (validationError) { setLocalError(validationError); return; }
    setLocalError('');
    const slug = effectiveWorkspace.toLowerCase();
    const result = await login(username.trim(), password, slug);
    if (result.success) {
      // Remember the slug so the next deep-link landing pre-fills it
      // instead of dropping the user on an empty "your-org" field.
      try { localStorage.setItem('ee_assistant_last_workspace', slug); } catch { /* ignore */ }
    }
    if (shouldPromptEmailVerification(result)) {
      setVerifyUsername(username.trim());
      setVerifyModalVisible(true);
    }
  };

  // ── Sign Up ───────────────────────────────────────────────
  const handleSignUp = async () => {
    setRegError('');
    setRegSuccess('');
    const validationError = validateSignUpRequiredFields({ regEmail, regUsername, regPassword, regConfirm })
      || validatePasswordPolicy(regPassword, policy);
    if (validationError) { setRegError(validationError); return; }
    setRegLoading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      await registerUser({
        email: regEmail.trim(),
        username: regUsername.trim(),
        password: regPassword,
        full_name: regFullName.trim() || regUsername.trim(),
        timezone: tz,
      });
      // Deliberately the same text whether or not this email already had an
      // account: the server no longer says which (pentest 2026-09), and the
      // owner of an existing address is told by email instead.
      setRegSuccess('Registration received. Check your email to verify your address — after that, an administrator reviews your access and you will receive an email once it is approved.');
      setRegEmail(''); setRegUsername(''); setRegPassword(''); setRegConfirm(''); setRegFullName('');
    } catch (err) {
      setRegError(err.message || 'Registration failed.');
    } finally {
      setRegLoading(false);
    }
  };

  // ── Resend Verification ───────────────────────────────────
  const handleResendVerification = async () => {
    try {
      await resendVerification(verifyUsername);
      // The server answers every resend the same way (unknown, verified,
      // throttled or sent), so this cannot claim more than that either.
      message.success('If that account is waiting for verification, a new link is on its way. Check your inbox (and spam folder).');
      setVerifyModalVisible(false);
    } catch (err) {
      message.error(err.message);
    }
  };

  const isEmailNotVerified = authError === EMAIL_NOT_VERIFIED;
  const isSsoPendingApproval = ssoErrorMatch === 'pending_approval';
  const isPendingApproval = authError === 'PENDING_ADMIN_APPROVAL' || isSsoPendingApproval;
  const isRejected = authError === 'ACCOUNT_REJECTED';
  const isApprovalState = isPendingApproval || isRejected;
  const isSsoError = Boolean(ssoErrorMatch) && !isSsoPendingApproval;
  const displayError = computeDisplayError({
    localError, authError, isEmailNotVerified, isApprovalState, isSsoError, ssoErrorMessage,
  });

  const { iconColor, labelColor, hintColor } = gateColors(themeConfig);

  const openResend = () => { setVerifyUsername(username.trim()); setVerifyModalVisible(true); };
  const clearLocalError = () => setLocalError('');
  const clearRegError = () => setRegError('');

  // Rendered above the password form on both tabs when SSO is enabled.
  const ssoBlock = ssoEnabled ? <SsoBlock workspace={workspace} /> : null;

  return (
    <div className="access-gate-overlay" data-testid="login-page-container">
      <div className="access-gate-container" data-testid="login-card">
        {/* Logo / Branding */}
        <div className="access-gate-logo" data-testid="login-header">
          {/* The product logo — the same file the browser tab uses. */}
          <img
            className="access-gate-icon"
            src={`${process.env.PUBLIC_URL}/favicon.svg`}
            alt="AI FinOps Master logo"
            width={72}
            height={72}
          />
          <Title level={2} className="access-gate-title">AI FinOps Master</Title>
          <Paragraph className="access-gate-subtitle">Manage your AI costs</Paragraph>
        </div>

        {/* Lock indicator */}
        <div className="access-gate-lock-badge">
          <LockOutlined style={{ fontSize: '13px', marginRight: '6px' }} />
          <Text style={{ fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 600, color: 'inherit' }}>
            Secure Access
          </Text>
        </div>

        {/* Tabs: Sign In / Sign Up */}
        <div className="access-gate-form">
          <Tabs activeKey={activeTab} onChange={setActiveTab} centered size="large" data-testid="login-tabs"
            items={[
              {
                key: 'signin',
                label: 'Sign In',
                children: (
                  <>
                    {ssoBlock}
                    <SignInAlerts
                      displayError={displayError} onCloseError={clearLocalError}
                      isEmailNotVerified={isEmailNotVerified} onResend={openResend}
                      isPendingApproval={isPendingApproval} isRejected={isRejected}
                    />
                    <SignInForm
                      singleTenant={_singleTenant} workspaceLocked={workspaceLocked} workspace={workspace}
                      setWorkspace={setWorkspace} setLocalError={setLocalError} handleSignIn={handleSignIn}
                      isLoading={isLoading} workspaceRef={workspaceRef} usernameRef={usernameRef}
                      username={username} setUsername={setUsername} password={password} setPassword={setPassword}
                      labelColor={labelColor} hintColor={hintColor} iconColor={iconColor}
                    />
                  </>
                ),
              },
              {
                key: 'signup',
                label: 'Sign Up',
                children: (
                  <>
                    {ssoBlock}
                    <SignUpForm
                      regError={regError} onCloseError={clearRegError} regSuccess={regSuccess} handleSignUp={handleSignUp}
                      regEmail={regEmail} setRegEmail={setRegEmail} regUsername={regUsername} setRegUsername={setRegUsername}
                      regFullName={regFullName} setRegFullName={setRegFullName} regPassword={regPassword}
                      setRegPassword={setRegPassword} regConfirm={regConfirm} setRegConfirm={setRegConfirm}
                      policy={policy} regLoading={regLoading} labelColor={labelColor} hintColor={hintColor} iconColor={iconColor}
                    />
                  </>
                ),
              },
            ]}
          />
        </div>

        <Divider style={{ margin: '8px 0 0 0' }} />
        <div className="access-gate-footer">
          <Text className="access-gate-footer-text">
            {activeTab === 'signin'
              ? "Don't have an account? Switch to Sign Up"
              : 'A verification email will be sent to your inbox'}
          </Text>
        </div>
      </div>

      {/* Email Verification Modal */}
      <Modal title="Email Not Verified" open={verifyModalVisible}
        zIndex={10000}
        onCancel={() => setVerifyModalVisible(false)}
        onOk={handleResendVerification} okText="Resend Verification Email">
        <p>Your email has not been verified yet. Please complete the email verification step to continue.</p>
        <p>Click <strong>Resend Verification Email</strong> to receive a new verification link.</p>
      </Modal>
    </div>
  );
}

export default AccessGate;
